import { Table, Tag, Button, Popover, message } from "antd";
import { Dispatch, SetStateAction, useEffect, useState } from "react";
import { ColumnsType } from "antd/es/table";
import { useTranslation } from "react-i18next";
import { DeleteOutlined, EditOutlined } from "@ant-design/icons";
import { UseMutateAsyncFunction, useMutation } from "react-query";
import { deleteCountries, getCountriesById } from "../../../api/countries";
import { AddEditCountrie } from "./AddEditCountries";
import { CountriesType } from "./countriesType";
import DeleteModal from "../../UI/ModalComponent/DeleteModal/deletemodal";
import { AxiosResponse } from "axios";

interface inventoryProps {
  CountriesMutation: UseMutateAsyncFunction<
    AxiosResponse<any, any>,
    unknown,
    {
      limit: number;
      q: string;
      isActive?: boolean | undefined;
    },
    unknown
  >;
  paginatedCountries: any;
  setPaginatedCountries: Dispatch<any>;
  isCountriesLoading: boolean;
  search: string;
  filter: boolean | undefined;
  page: number;
  setPage: Dispatch<SetStateAction<number>>;
  tableKey: string;
}
interface CountriesInterface {
  id: number;
  name: string;
  abbreviation: string;
  areaCode: string;
  isActive: boolean;
}
const CountriesList = ({
  CountriesMutation,
  paginatedCountries,
  setPaginatedCountries,
  isCountriesLoading,
  search,
  filter,
  page,
  setPage,
  tableKey,
}: inventoryProps) => {
  const [t] = useTranslation("common");
  const [openCountrie, setOpenCountrie] = useState(false);
  const [Countries, setCountries] = useState<CountriesType>();
  const [selectedCountrie, setSelectedCountrie] = useState<any>();
  const [prevPage, setPrevPage] = useState<number>(0);
  const [maxPage, setMaxPage] = useState<number>(0);
  const [, setError] = useState("");
  const Edit = <div>{t("edit")}</div>;
  const Delete = <div>{t("delete")}</div>;

  useEffect(() => {
    page > prevPage &&
      maxPage < page &&
      CountriesMutation({
        limit: page * 8 + 8,
        q: search,
        isActive: filter,
      }).then((res) => {
        if (page > maxPage) setMaxPage(page);
        setPaginatedCountries(res.data?.data);
      });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [page, filter]);

  const getCountriesMutation = useMutation("getCountries", getCountriesById);
  function fillCountry(id: any) {
    getCountriesMutation.mutate(id, {
      onSuccess: (res) => {
        setCountries(res.data);
        setOpenCountrie(true);
      },
    });
  }
  const onError = (error: any, context: unknown) => {
    if (setError) {
      setError(error.response.data.message);
      message.error({
        content: error.response.data.message,
        duration: 10,
        onClick: () => {
          message.destroy();
        },
      });
    }
  };
  const deleteCountriesMutation = useMutation(
    " deleteCountries",
    deleteCountries,
    {
      onSuccess: () => {
        CountriesMutation({
          limit: page * 8 + 8,
          q: search,
          isActive: filter,
        }).then((res: any) => {
          setPaginatedCountries(res.data?.data);
          message.success("Complete successfully");
        });
      },
      onError,
    }
  );

  const [open, setOpen] = useState(false);
  const [idToDelete, setIdToDelete] = useState<string | undefined>(undefined);
  const [nameToDelete, setNameToDelete] = useState<any>();
  const DeleteCountries = (id: string | undefined, name: string): void => {
    setOpen(true);
    setIdToDelete(id);
    setNameToDelete(name);
  };

  const handleOk = () => {
    if (idToDelete) {
      deleteCountriesMutation.mutate({ id: idToDelete });
    }
    setOpen(false);
  };

  const columns: ColumnsType<CountriesInterface> = [
    {
      title: `${t("name")}`,
      dataIndex: "name",
      // width: "25rem",
      key: "name",
      className: "table-header",
    },
    {
      title: `${t("abbreviation")}`,
      dataIndex: "abbreviation",
      width: "7rem",
      key: "abbreviation",
    },
    {
      title: `${t("area-code")}`,
      dataIndex: "areaCode",
      key: "areacode",
      width: "6rem",
    },
    {
      title: `${t("status")}`,
      dataIndex: "isActive",
      align: "center",
      width: "5rem",
      key: "isActive",
      render: (record: any) =>
        record ? (
          <Tag className="active">Active</Tag>
        ) : (
          <Tag className="inactive">Inactive</Tag>
        ),
    },
    {
      title: `${t("action")}`,
      key: "action",
      fixed: "right",
      width: "6rem",
      align: "center",
      render: (record: any) => (
        <>
          <Popover content={Edit} mouseEnterDelay={0} mouseLeaveDelay={0}>
            <Button
              type="text"
              className="Button"
              disabled={true}
              icon={
                <EditOutlined style={{ color: "#212121", fontSize: "14px" }} />
              }
              onClick={() => {
                setSelectedCountrie(record.id);
                fillCountry(record.id);
              }}
            />
          </Popover>
          <Popover content={Delete} mouseEnterDelay={0} mouseLeaveDelay={0}>
            <Button
              disabled={true}
              className="Button"
              type="text"
              icon={
                <DeleteOutlined
                  style={{ color: "#F5222D", fontSize: "14px" }}
                />
              }
              onClick={() => DeleteCountries(record?.id, record?.name)}
            />
          </Popover>
        </>
      ),
    },
  ];
  return (
    <>
      <DeleteModal
        open={open}
        setOpen={setOpen}
        name={nameToDelete}
        onOk={handleOk}
      />
      {openCountrie && Countries && (
        <AddEditCountrie
          openCountrie={openCountrie}
          Countries={Countries}
          setOpenCountrie={setOpenCountrie}
          id={selectedCountrie}
          search={search}
          filter={filter}
          setPaginatedCountries={setPaginatedCountries}
          CountriesMutation={CountriesMutation}
        />
      )}
      <Table
        key={tableKey}
        scroll={{ x: 1000 }}
        columns={columns}
        dataSource={paginatedCountries}
        pagination={{ pageSize: 8 }}
        loading={isCountriesLoading}
        rowClassName={(record, index) =>
          index % 2 === 0
            ? "table-row-light table-row"
            : "table-row-dark table-row"
        }
        onChange={(selectedPage) => {
          setPrevPage(page);
          setPage(selectedPage?.current ?? 1);
        }}
        rowKey={(record) => record.id}
      />
    </>
  );
};

export default CountriesList;
